import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import CrmWorkflowService from '../../services/CrmWorkflowService';
import LeadService from '../../services/LeadService';
import RegisterUserService from '../../services/RegisterUserService';
import ToastService from '../../services/ToastService';
import { getVisitStatus, getDealStage, DEAL_STAGES } from '../../constants/crmWorkflowConstants';
import { fmtPrice } from '../../constants/propertyConstants';

const lbl = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#8392ab',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #e9ecef',
  borderRadius: '10px',
  fontSize: '14px',
  color: '#344767',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
  border: 'none',
  borderRadius: '10px',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: '700',
  color: '#fff',
  cursor: 'pointer',
};

function parsePrefs(json) {
  const empty = {
    preferredLocations: '',
    propertyTypes: '',
    buyingTimeline: '',
    loanRequired: '',
  };
  if (!json || typeof json !== 'string') return empty;
  try {
    const o = JSON.parse(json);
    return {
      preferredLocations: o.preferredLocations || '',
      propertyTypes: o.propertyTypes || '',
      buyingTimeline: o.buyingTimeline || '',
      loanRequired: o.loanRequired || '',
    };
  } catch {
    return empty;
  }
}

function stringifyPrefs(baseJson, prefs) {
  let existing = {};
  if (baseJson && typeof baseJson === 'string') {
    try {
      existing = JSON.parse(baseJson);
    } catch {
      existing = {};
    }
  }
  return JSON.stringify({
    ...existing,
    preferredLocations: prefs.preferredLocations,
    propertyTypes: prefs.propertyTypes,
    buyingTimeline: prefs.buyingTimeline,
    loanRequired: prefs.loanRequired,
  });
}

/** Active tenant users who can own buyers: Admins (incl. legacy role id 3) and Employees. */
function normalizeSalesStaff(rawList, options = {}) {
  const { ensureUserId } = options;
  const arr = Array.isArray(rawList) ? rawList : [];
  const SALES_ROLES = new Set([2, 3, 4]);
  const uid = (u) => Number(u.id ?? u.Id);
  let list = arr
    .filter((u) => {
      const active = (u.isActive ?? u.IsActive) !== false;
      const rid = Number(u.roleId ?? u.RoleId ?? 0);
      return active && SALES_ROLES.has(rid);
    })
    .sort((a, b) => {
      const na = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || '';
      const nb = [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email || '';
      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
  if (ensureUserId != null && ensureUserId !== '' && !Number.isNaN(Number(ensureUserId))) {
    const idNum = Number(ensureUserId);
    if (!list.some((u) => uid(u) === idNum)) {
      const extra = arr.find((u) => uid(u) === idNum);
      if (extra) list = [...list, extra];
    }
  }
  return list;
}

/** Single buyer 360° — profile, source lead, visits, deals, lead activity. */
export default function BuyerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const toastService = new ToastService(toast);
  const clientId = AuthService.getLoggedInClientId();

  const [loading, setLoading] = useState(true);
  const [buyer, setBuyer] = useState(null);
  const [users, setUsers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [deals, setDeals] = useState([]);
  const [leadHistory, setLeadHistory] = useState([]);
  /** keyed by deal id — draft lost reason before marking Closed lost */
  const [lostDraft, setLostDraft] = useState({});

  const [draft, setDraft] = useState({});
  const [prefs, setPrefs] = useState(parsePrefs());

  const buyerId = parseInt(id, 10);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId || Number.isNaN(buyerId)) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const cid = Number(clientId);
        const [b, staffRaw] = await Promise.all([
          CrmWorkflowService.getBuyerById(buyerId),
          RegisterUserService.getUsersByClientId(cid).catch(() => []),
        ]);
        if (cancelled) return;
        setBuyer(b);
        const assignedUid = b?.assignedUserId ?? b?.AssignedUserId;
        setUsers(normalizeSalesStaff(staffRaw, { ensureUserId: assignedUid }));
        if (b) {
          setDraft({
            firstName: b.firstName ?? '',
            lastName: b.lastName ?? '',
            email: b.email ?? '',
            phone: b.phone ?? '',
            budgetMin: b.budgetMin ?? '',
            budgetMax: b.budgetMax ?? '',
            requirementNotes: b.requirementNotes ?? '',
            financingNotes: b.financingNotes ?? '',
            assignedUserId:
              assignedUid === null || assignedUid === undefined || assignedUid === '' ? '' : String(assignedUid),
          });
          setPrefs(parsePrefs(b.preferencesJson));
          const leadId = b.sourceLeadId;
          const [visits, pipeline, hist] = await Promise.all([
            CrmWorkflowService.getInterestsByLead(leadId),
            CrmWorkflowService.getDealsByBuyer(b.id),
            LeadService.getLeadHistoryByLeadId(leadId).catch(() => []),
          ]);
          if (!cancelled) {
            setInterests(visits);
            setDeals(pipeline);
            setLeadHistory(Array.isArray(hist) ? hist : []);
          }
        }
      } catch (e) {
        console.error(e);
        toastService.showError('Error', 'Could not load buyer.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buyerId, clientId]);

  const saveProfile = async () => {
    if (!buyer?.id) return;
    try {
      const preferencesJson = stringifyPrefs(buyer.preferencesJson, prefs);
      await CrmWorkflowService.updateBuyer({
        id: buyer.id,
        clientId: buyer.clientId,
        sourceLeadId: buyer.sourceLeadId,
        firstName: draft.firstName || '',
        lastName: draft.lastName || '',
        email: draft.email || '',
        phone: draft.phone || '',
        budgetMin: draft.budgetMin === '' ? null : Number(draft.budgetMin),
        budgetMax: draft.budgetMax === '' ? null : Number(draft.budgetMax),
        requirementNotes: draft.requirementNotes || '',
        financingNotes: draft.financingNotes || '',
        preferencesJson,
        documentsJson: buyer.documentsJson || null,
        assignedUserId: draft.assignedUserId === '' ? null : Number(draft.assignedUserId),
      });
      toastService.showSuccess('Saved', 'Buyer profile updated.');
      const refreshed = await CrmWorkflowService.getBuyerById(buyer.id);
      setBuyer(refreshed);
    } catch (e) {
      console.error(e);
      toastService.showError('Error', 'Save failed.');
    }
  };

  const patchDealStage = async (dealRow, stage, explicitLostReason) => {
    const dealId = dealRow.id ?? dealRow.Id;
    if (!dealId || !buyer?.id) return;
    const lr =
      stage === 'ClosedLost'
        ? explicitLostReason !== undefined && explicitLostReason !== null
          ? explicitLostReason
          : lostDraft[dealId] || ''
        : '';
    try {
      await CrmWorkflowService.updateDealStage(dealId, stage, lr);
      toastService.showSuccess('Stage', 'Deal stage updated.');
      const pipeline = await CrmWorkflowService.getDealsByBuyer(buyer.id);
      setDeals(Array.isArray(pipeline) ? pipeline : []);
    } catch (e) {
      console.error(e);
      toastService.showError('Error', e?.response?.data || 'Stage update failed.');
    }
  };

  if (!clientId) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted">Sign in with a tenant account.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">
        <Toast ref={toast} />
        <i className="fas fa-circle-notch fa-spin me-2" />
        Loading buyer…
      </div>
    );
  }

  if (!buyer) {
    return (
      <div className="container-fluid py-4">
        <Toast ref={toast} />
        <p className="text-muted">Buyer not found.</p>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/buyers')}>
          Back to Buyers
        </button>
      </div>
    );
  }

  const name = [buyer.firstName, buyer.lastName].filter(Boolean).join(' ') || 'Buyer';

  return (
    <div style={{ padding: '8px 8px 40px', background: '#f4f6fb', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-light border rounded-pill px-3"
            onClick={() => navigate('/buyers')}
          >
            <i className="fas fa-arrow-left me-2" />
            Buyers
          </button>
          <div>
            <h4 className="mb-0 fw-bold text-dark">{name}</h4>
            <p className="mb-0 small text-muted">
              Source lead{' '}
              {buyer.sourceLeadCode ? (
                <span className="badge bg-light text-dark border">{buyer.sourceLeadCode}</span>
              ) : (
                `#${buyer.sourceLeadId}`
              )}
              {' · '}
              <Link to={`/leads/${buyer.sourceLeadId}`} className="text-decoration-none">
                Open original lead
              </Link>
              {' · '}
              <Link to={`/editlead/${buyer.sourceLeadId}`} className="text-decoration-none">
                Edit lead record
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <h6 className="text-uppercase text-muted fw-bold mb-3 small" style={{ letterSpacing: '0.06em' }}>
                Buyer profile
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label style={lbl}>First name</label>
                  <input style={inp} value={draft.firstName} onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Last name</label>
                  <input style={inp} value={draft.lastName} onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Email</label>
                  <input style={inp} type="email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Phone</label>
                  <input style={inp} value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Budget min</label>
                  <input style={inp} type="number" value={draft.budgetMin} onChange={(e) => setDraft((d) => ({ ...d, budgetMin: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Budget max</label>
                  <input style={inp} type="number" value={draft.budgetMax} onChange={(e) => setDraft((d) => ({ ...d, budgetMax: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label style={lbl}>Assigned salesperson</label>
                  <select
                    style={{ ...inp, cursor: 'pointer' }}
                    value={draft.assignedUserId === '' || draft.assignedUserId == null ? '' : String(draft.assignedUserId)}
                    onChange={(e) => setDraft((d) => ({ ...d, assignedUserId: e.target.value }))}
                  >
                    <option value="">— Not assigned —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `User #${u.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label style={lbl}>Requirements summary</label>
                  <textarea
                    style={{ ...inp, minHeight: 80, resize: 'vertical' }}
                    value={draft.requirementNotes}
                    onChange={(e) => setDraft((d) => ({ ...d, requirementNotes: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label style={lbl}>Preferred locations</label>
                  <input
                    style={inp}
                    value={prefs.preferredLocations}
                    onChange={(e) => setPrefs((p) => ({ ...p, preferredLocations: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Property types</label>
                  <input
                    style={inp}
                    value={prefs.propertyTypes}
                    onChange={(e) => setPrefs((p) => ({ ...p, propertyTypes: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label style={lbl}>Buying timeline</label>
                  <input
                    style={inp}
                    placeholder="e.g. 3 months"
                    value={prefs.buyingTimeline}
                    onChange={(e) => setPrefs((p) => ({ ...p, buyingTimeline: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label style={lbl}>Loan / financing notes</label>
                  <textarea
                    style={{ ...inp, minHeight: 72, resize: 'vertical' }}
                    value={draft.financingNotes}
                    onChange={(e) => setDraft((d) => ({ ...d, financingNotes: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label style={lbl}>Loan required?</label>
                  <input
                    style={inp}
                    placeholder="Yes / No / Bank name…"
                    value={prefs.loanRequired}
                    onChange={(e) => setPrefs((p) => ({ ...p, loanRequired: e.target.value }))}
                  />
                </div>
              </div>
              <button type="button" className="mt-4" style={btnPrimary} onClick={saveProfile}>
                <i className="fas fa-save me-2" />
                Save profile
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <h6 className="text-uppercase text-muted fw-bold mb-3 small" style={{ letterSpacing: '0.06em' }}>
                  Deals (transactions)
                </h6>
              <p className="small text-muted mb-3">
                Update the <strong>stage</strong> per property deal below. You can also manage deals from{' '}
                <strong>Assigned Leads</strong> → open the assignment → <strong>Properties &amp; Deals</strong> tab.
              </p>
              {deals.length === 0 ? (
                <p className="text-muted small mb-0">No deals yet.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr className="small text-muted text-uppercase">
                        <th>Property</th>
                        <th>Value</th>
                        <th>Stage</th>
                        <th>Lost reason</th>
                        <th>Expected close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deals.map((d) => {
                        const dealId = d.id ?? d.Id;
                        const stageKey = d.stage ?? d.Stage ?? '';
                        const st = getDealStage(stageKey);
                        const selectVal = DEAL_STAGES.some((s) => s.id === stageKey)
                          ? stageKey
                          : stageKey || 'Negotiation';
                        const exp =
                          d.expectedCloseDate ?? d.ExpectedCloseDate
                            ? new Date(d.expectedCloseDate ?? d.ExpectedCloseDate).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '—';
                        return (
                          <tr key={dealId}>
                            <td>
                              <div className="fw-semibold">{d.propertyName ?? d.PropertyName}</div>
                              <div className="small text-muted">{d.projectLabel ?? d.ProjectLabel}</div>
                              {(d.dealSourceType === 'ChannelPartner' || d.DealSourceType === 'ChannelPartner') && (
                                <div className="small mt-1" style={{ color: '#825ee4', fontWeight: 600 }}>
                                  Partner: {(d.builderCompanyName ?? d.BuilderCompanyName) || '—'} · Est.{' '}
                                  {fmtPrice(d.expectedCommissionAmount ?? d.ExpectedCommissionAmount)} ·{' '}
                                  {(d.commissionStatus ?? d.CommissionStatus) || 'Pending'}
                                </div>
                              )}
                            </td>
                            <td>{fmtPrice(d.dealValue ?? d.DealValue)}</td>
                            <td style={{ minWidth: '160px' }}>
                              <select
                                className="form-select form-select-sm"
                                style={{ ...inp, padding: '8px 10px', fontSize: '13px' }}
                                value={selectVal}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  if (next === 'ClosedLost') {
                                    const reason = window.prompt('Lost reason (optional)') || '';
                                    setLostDraft((prev) => ({ ...prev, [dealId]: reason }));
                                    void patchDealStage(d, next, reason);
                                  } else {
                                    void patchDealStage(d, next, '');
                                  }
                                }}
                              >
                                {stageKey && !DEAL_STAGES.some((s) => s.id === stageKey) ? (
                                  <option value={stageKey}>{getDealStage(stageKey).label}</option>
                                ) : null}
                                {DEAL_STAGES.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.label}
                                  </option>
                                ))}
                              </select>
                              <div className="small mt-1" style={{ fontWeight: 700, color: st.color }}>
                                {st.label}
                              </div>
                            </td>
                            <td style={{ maxWidth: '200px' }}>
                              <input
                                className="form-control form-control-sm"
                                style={{ ...inp, padding: '8px 10px', fontSize: '12px' }}
                                placeholder="If lost…"
                                value={lostDraft[dealId] ?? d.lostReason ?? d.LostReason ?? ''}
                                onChange={(e) =>
                                  setLostDraft((prev) => ({ ...prev, [dealId]: e.target.value }))
                                }
                              />
                            </td>
                            <td className="small text-muted">{exp}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <h6 className="text-uppercase text-muted fw-bold mb-3 small" style={{ letterSpacing: '0.06em' }}>
                Inventory viewed / visits
              </h6>
              <p className="small text-muted mb-3">
                Mapped from the original inquiry lead — same properties you linked while they were a lead.
              </p>
              {interests.length === 0 ? (
                <p className="text-muted small mb-0">No property matches recorded.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr className="small text-muted text-uppercase">
                        <th>Property</th>
                        <th>Visit status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interests.map((row) => {
                        const vs = getVisitStatus(row.visitStatus);
                        return (
                          <tr key={row.id}>
                            <td>
                              <div className="fw-semibold">{row.propertyName}</div>
                              <div className="small text-muted">{row.projectLabel}</div>
                            </td>
                            <td>
                              <span style={{ fontWeight: 600, color: vs.color }}>{vs.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm border-0" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <h6 className="text-uppercase text-muted fw-bold mb-3 small" style={{ letterSpacing: '0.06em' }}>
                Lead activity (history)
              </h6>
              {leadHistory.length === 0 ? (
                <p className="text-muted small mb-0">No history rows.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {[...leadHistory]
                    .sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0))
                    .slice(0, 25)
                    .map((h, i) => (
                      <li key={i} className="list-group-item px-0 border-light">
                        <div className="small text-muted mb-1">
                          {h.createdOn && new Date(h.createdOn).getFullYear() > 1
                            ? new Date(h.createdOn).toLocaleString('en-IN')
                            : ''}
                        </div>
                        <div style={{ fontSize: '14px' }}>{h.comment || '—'}</div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
